import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { StatusBadge } from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Plus } from "lucide-react";
import { localeOf } from "../../i18n";

const empty = {
  title: "", description: "", campaign_id: "", partner_id: "", starts_at: "", ends_at: "",
  location: "", agenda: "", objectives: "", capacity: 20, team_ids: [], leader_ids: [],
  attendance_points: 10, status: "published", social_image_url: "",
};

export default function StaffEvents() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [partners, setPartners] = useState([]);
  const [teams, setTeams] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const locale = localeOf(i18n.language);

  const load = () => api.get("/events").then(({ data }) => setEvents(data));

  useEffect(() => {
    load();
    api.get("/campaigns").then(({ data }) => setCampaigns(data)).catch(() => {});
    api.get("/partners").then(({ data }) => setPartners(data)).catch(() => {});
    api.get("/teams").then(({ data }) => setTeams(data)).catch(() => {});
    api.get("/users", { params: { role: "team_leader" } }).then(({ data }) => setLeaders(data)).catch(() => {});
  }, []);

  const save = async () => {
    const payload = {
      ...form,
      capacity: Number(form.capacity) || 0,
      attendance_points: Number(form.attendance_points) || 0,
      starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : "",
      ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
      campaign_id: form.campaign_id || null,
      partner_id: form.partner_id || null,
      social_image_url: (form.social_image_url || "").trim() || null,
    };
    try {
      if (editId) await api.patch(`/events/${editId}`, payload);
      else await api.post("/events", payload);
      toast.success(t("common.saved"));
      setOpen(false); setForm(empty); setEditId(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const startEdit = (e) => {
    setEditId(e.id);
    setForm({
      ...empty, ...e,
      campaign_id: e.campaign_id || "", partner_id: e.partner_id || "",
      starts_at: e.starts_at ? e.starts_at.slice(0, 16) : "",
      ends_at: e.ends_at ? e.ends_at.slice(0, 16) : "",
      team_ids: e.team_ids || [], leader_ids: e.leader_ids || [],
    });
    setOpen(true);
  };

  const toggleIn = (key, id) => setForm((f) => ({
    ...f, [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id],
  }));

  return (
    <div className="space-y-5" data-testid="staff-events-page">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-extrabold">{t("nav.events")}</h1>
        {user?.role === "admin" && (
          <Button data-testid="new-event-btn" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }}
            className="h-12 rounded-full font-bold">
            <Plus className="mr-1 h-4 w-4" />{t("admin.newEvent")}
          </Button>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {events.map((e) => (
          <div key={e.id} className="vy-card p-5" data-testid={`staff-event-card-${e.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <Link to={`/staff/events/${e.id}`} className="font-display text-lg font-bold hover:text-primary">
                  {e.title}
                </Link>
                <p className="text-xs text-muted-foreground">{fmtDate(e.starts_at, locale)} · {e.location}</p>
              </div>
              <StatusBadge status={e.status} />
            </div>
            <div className="mt-3 flex flex-wrap gap-3 text-xs font-semibold text-muted-foreground">
              <span>{e.confirmed_count}/{e.capacity} {t("events.confirmedCount")}</span>
              <span>{e.checked_in_count} {t("events.checkedIn")}</span>
              <span>{e.mission_count} {t("events.missions").toLowerCase()}</span>
              <span className="rounded-full bg-muted px-2 py-0.5 text-foreground" data-testid={`event-short-code-${e.id}`}>
                {e.short_code}
              </span>
            </div>
            <div className="mt-4 flex gap-2">
              <Button asChild variant="outline" className="h-11 rounded-full font-semibold">
                <Link to={`/staff/events/${e.id}`} data-testid={`manage-event-btn-${e.id}`}>{t("common.actions")}</Link>
              </Button>
              {user?.role === "admin" && (
                <Button data-testid={`edit-event-btn-${e.id}`} variant="ghost" className="h-11 rounded-full"
                  onClick={() => startEdit(e)}>{t("common.edit")}</Button>
              )}
            </div>
          </div>
        ))}
        {events.length === 0 && <p className="vy-card p-6 text-sm text-muted-foreground">{t("events.noEvents")}</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="event-form-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>{editId ? t("common.edit") : t("admin.newEvent")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="event-title-input" placeholder={t("admin.name")} className="h-12"
              value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <Textarea data-testid="event-description-input" placeholder={t("events.detail")} rows={2}
              value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold">{t("common.start")}</label>
                <Input data-testid="event-starts-input" type="datetime-local" className="h-12"
                  value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-bold">{t("common.end")}</label>
                <Input data-testid="event-ends-input" type="datetime-local" className="h-12"
                  value={form.ends_at || ""} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} />
              </div>
            </div>
            <Input data-testid="event-location-input" placeholder={t("events.location")} className="h-12"
              value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            <div>
              <label className="text-xs font-bold">Image sociale — URL 1200 × 630</label>
              <Input data-testid="event-social-image-input" placeholder="https://…/image.png" className="h-12"
                value={form.social_image_url || ""}
                onChange={(e) => setForm({ ...form, social_image_url: e.target.value })} />
            </div>
            <Textarea data-testid="event-agenda-input" placeholder={t("events.agenda")} rows={2}
              value={form.agenda || ""} onChange={(e) => setForm({ ...form, agenda: e.target.value })} />
            <Textarea data-testid="event-objectives-input" placeholder={t("events.objectives")} rows={2}
              value={form.objectives || ""} onChange={(e) => setForm({ ...form, objectives: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold">{t("events.capacity")}</label>
                <Input data-testid="event-capacity-input" type="number" className="h-12" value={form.capacity}
                  onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-bold">{t("admin.attendancePoints")}</label>
                <Input data-testid="event-attendance-points-input" type="number" className="h-12"
                  value={form.attendance_points}
                  onChange={(e) => setForm({ ...form, attendance_points: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold">{t("events.campaign")}</label>
              <select data-testid="event-campaign-select" className="h-12 w-full rounded-xl border border-input px-3"
                value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })}>
                <option value="">{t("common.none")}</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold">{t("events.partner")}</label>
              <select data-testid="event-partner-select" className="h-12 w-full rounded-xl border border-input px-3"
                value={form.partner_id} onChange={(e) => setForm({ ...form, partner_id: e.target.value })}>
                <option value="">{t("common.none")}</option>
                {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold">{t("nav.teams")}</label>
              <div className="mt-1 flex flex-wrap gap-2">
                {teams.map((tm) => (
                  <button key={tm.id} type="button" data-testid={`event-team-toggle-${tm.id}`}
                    onClick={() => toggleIn("team_ids", tm.id)}
                    className={`h-10 rounded-full px-4 text-sm font-semibold ${
                      form.team_ids.includes(tm.id) ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                    {tm.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold">{t("admin.team_leader")}</label>
              <div className="mt-1 flex flex-wrap gap-2">
                {leaders.map((l) => (
                  <button key={l.id} type="button" data-testid={`event-leader-toggle-${l.id}`}
                    onClick={() => toggleIn("leader_ids", l.id)}
                    className={`h-10 rounded-full px-4 text-sm font-semibold ${
                      form.leader_ids.includes(l.id) ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                    {l.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold">{t("status.published")}</label>
              <select data-testid="event-status-select" className="h-12 w-full rounded-xl border border-input px-3"
                value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="published">{t("status.published")}</option>
                <option value="draft">{t("status.draft")}</option>
                <option value="cancelled">{t("status.cancelled")}</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="event-save-btn" onClick={save} className="h-12 rounded-full font-bold">
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
